<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="1200"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '28rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div class="crc-form-dialog-header__icon bg-sky-50 dark:bg-sky-500/15 text-sky-600 dark:text-sky-400">
          <i class="pi pi-id-card text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">Edit employment type</div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-fields">
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="employment-type-edit-code"
              v-model="draft.code"
              :disabled="saving"
              maxlength="50"
              autocomplete="off"
              :invalid="submitted && !draft.code?.trim()"
              class="w-full rounded-md"
            />
            <label for="employment-type-edit-code" class="text-sm font-medium">
              Code <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.code?.trim()" class="text-xs text-red-500 pl-1">
            Code is required.
          </small>

          <FloatLabel variant="on" class="w-full">
            <InputText
              id="employment-type-edit-label"
              v-model="draft.label"
              :disabled="saving"
              maxlength="100"
              autocomplete="off"
              class="w-full rounded-md"
            />
            <label for="employment-type-edit-label" class="text-sm font-medium">Display label</label>
          </FloatLabel>

          <p v-if="draft.employee_count > 0" class="text-xs text-surface-500 dark:text-surface-400 m-0 pl-1">
            Used by {{ draft.employee_count }} employee{{ draft.employee_count === 1 ? '' : 's' }}.
            Renaming updates existing employee records.
          </p>
          <p v-if="draft.is_system" class="text-xs text-amber-600 dark:text-amber-400 m-0 pl-1">
            System type — kept for organogram parity; prefer deactivate over delete when in use.
          </p>
        </div>
      </section>

      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-status">
          <div>
            <div class="crc-form-dialog-status__title">Active</div>
            <div class="crc-form-dialog-status__hint">
              Inactive types are hidden from employee create/edit dropdowns.
            </div>
          </div>
          <ToggleSwitch v-model="draft.is_active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="visibleProxy = false"
          />
          <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import ToggleSwitch from 'primevue/toggleswitch';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';

defineOptions({ name: 'EmploymentTypeEditModal' });

const props = defineProps({
  visible: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(['update:visible', 'save']);

const submitted = ref(false);

const draft = reactive({
  id: null,
  code: '',
  label: '',
  is_active: true,
  is_system: false,
  employee_count: 0,
});

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

const resetDraft = () => {
  draft.id = null;
  draft.code = '';
  draft.label = '';
  draft.is_active = true;
  draft.is_system = false;
  draft.employee_count = 0;
  submitted.value = false;
};

const onClose = () => {
  if (!props.saving) resetDraft();
};

const openEdit = (row) => {
  draft.id = row?.id ?? null;
  draft.code = row?.code ?? row?.value ?? '';
  draft.label = row?.label ?? draft.code;
  draft.is_active = row?.is_active !== false;
  draft.is_system = !!row?.is_system;
  draft.employee_count = Number(row?.employee_count ?? 0);
  submitted.value = false;
};

const handleSubmit = () => {
  submitted.value = true;
  const code = draft.code?.trim();
  if (!code) return;
  emit('save', {
    id: draft.id,
    code,
    label: (draft.label?.trim() || code),
    is_active: draft.is_active ? 1 : 0,
  });
};

defineExpose({ openEdit });
</script>
